import {
    Cancel,
    ErrorOutline,
    HourglassTop,
    Lock,
    Verified,
} from "@mui/icons-material";
import { Box, Paper, Stack, Typography } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import { AcxExpandableText } from "components/UI/AcxExpandableText";
import type { LLMClassifierImplementation } from "models/LLMs/LLMClassifierImplementation";
import { ApprovalStatus } from "models/LLMs/LLMClassifierApproval";
import moment from "moment";
import {
    canReviewApprovalRequest,
    isApprovalRequester,
} from "../../utils/llmClassifierApproval";
import { llmClassifierVersionCreator } from "../../utils/llmClassifierVersionDisplay";

const STATUS_ICONS: Partial<
    Record<ApprovalStatus, { Icon: typeof Lock; color: string }>
> = {
    [ApprovalStatus.Draft]: { Icon: Lock, color: "yellow.dark" },
    [ApprovalStatus.Approved]: { Icon: Verified, color: "success.700" },
    [ApprovalStatus.ChangesRequested]: {
        Icon: ErrorOutline,
        color: "error.main",
    },
    [ApprovalStatus.Rejected]: { Icon: Cancel, color: "error.main" },
};

export function ApprovalStatusPanel(props: {
    version: LLMClassifierImplementation;
    status: ApprovalStatus;
    currentUserEmail: string | undefined;
    currentUserId: string | undefined;
    canReview: boolean;
    requester: boolean;
    isSaving: boolean;
    /** One line while the reviewer still has diff left to read. */
    isCondensed?: boolean;
    /** Set while the diff is unread, which blocks the approval actions. */
    actionDisabledReason?: string;
    onRequestApproval: () => void;
    onCancelRequest: () => void;
    onReviewVersion: () => void;
    onViewChangeRecord: () => void;
}) {
    const {
        version,
        status,
        currentUserEmail,
        currentUserId,
        canReview,
        requester,
        isSaving,
        isCondensed,
        actionDisabledReason,
    } = props;
    const request = version.approvalRequest;
    const event = version.latestApprovalEvent;
    const reviewerNames = request?.requestedApprovers
        .map((reviewer) => reviewer.displayName)
        .join(", ");
    const canReviewThisRequest = canReviewApprovalRequest({
        canReview,
        isRequester: requester,
        request,
        currentUserId,
    });
    const eventDate = event?.createdOn
        ? moment.utc(event.createdOn).local().format("MMM D · HH:mm")
        : undefined;
    const isLastDecisionMaker =
        (status === ApprovalStatus.Rejected ||
            status === ApprovalStatus.ChangesRequested) &&
        isApprovalRequester(event, currentUserEmail);
    const statusIcon = STATUS_ICONS[status];
    const showsRequestApproval =
        (status === ApprovalStatus.Draft ||
            status === ApprovalStatus.Rejected ||
            status === ApprovalStatus.ChangesRequested) &&
        !isLastDecisionMaker;
    const showsReviewVersion =
        canReviewThisRequest && status === ApprovalStatus.AwaitingApproval;

    const showsDisabledHint =
        !!actionDisabledReason && (showsRequestApproval || showsReviewVersion);

    function panelTitle() {
        switch (status) {
            case ApprovalStatus.AwaitingApproval:
                return reviewerNames
                    ? `Awaiting approval from ${reviewerNames}`
                    : "Awaiting approval";
            case ApprovalStatus.Approved: {
                const approvedBy = event?.createdByName
                    ? `Approved by ${event.createdByName}`
                    : "Approved";
                return eventDate ? `${approvedBy} · ${eventDate}` : approvedBy;
            }
            case ApprovalStatus.Rejected:
                return "Approval rejected";
            case ApprovalStatus.ChangesRequested: {
                const requestedBy = event?.createdByName
                    ? `Changes requested by ${event.createdByName}`
                    : "Changes requested";
                return eventDate
                    ? `${requestedBy} · ${eventDate}`
                    : requestedBy;
            }
            default:
                return "Publishing is blocked until this version is approved";
        }
    }

    function panelDetail() {
        if (status === ApprovalStatus.AwaitingApproval && request) {
            return `Requested${
                request.createdByName ? ` by ${request.createdByName}` : ""
            } · ${moment
                .utc(request.createdOn)
                .local()
                .format("MMM D · HH:mm")}`;
        }
        if (status === ApprovalStatus.Approved) {
            return "Signed electronically · dual-person control satisfied";
        }
        if (
            (status === ApprovalStatus.Rejected ||
                status === ApprovalStatus.ChangesRequested) &&
            event
        ) {
            // ChangesRequested's title already carries the date, so only Rejected repeats it here.
            return [
                event.comment,
                status === ApprovalStatus.Rejected ? eventDate : undefined,
            ]
                .filter(Boolean)
                .join(" · ");
        }
        return `Changes by ${llmClassifierVersionCreator(
            version,
        )} · a different team member must sign off`;
    }

    return (
        <Paper
            variant="outlined"
            sx={(theme) => ({
                borderRadius: 3,
                borderColor: theme.palette.gray[200],
                boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.05)",
            })}
        >
            <Stack
                direction={isCondensed ? "row" : { xs: "column", md: "row" }}
                alignItems={
                    isCondensed ? "center" : { xs: "stretch", md: "center" }
                }
                gap={isCondensed ? 1.5 : 2}
                px={isCondensed ? 2 : 3}
                py={isCondensed ? 1.25 : 2.5}
            >
                {statusIcon ? (
                    <statusIcon.Icon
                        sx={{
                            color: statusIcon.color,
                            fontSize: isCondensed ? 20 : 22,
                            flexShrink: 0,
                        }}
                    />
                ) : isCondensed ? (
                    <HourglassTop
                        sx={{
                            color: "warning.main",
                            fontSize: 20,
                            flexShrink: 0,
                        }}
                    />
                ) : (
                    <Box
                        sx={(theme) => ({
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            flexShrink: 0,
                            backgroundColor: theme.palette.yellow.background,
                        })}
                    >
                        <HourglassTop
                            sx={{ color: "warning.main", fontSize: 22 }}
                        />
                    </Box>
                )}

                <Box flex={1} minWidth={0}>
                    <Stack
                        direction="row"
                        gap={1}
                        alignItems="center"
                        flexWrap="wrap"
                    >
                        <Typography
                            variant={isCondensed ? "body2" : "subtitle2"}
                            fontWeight={600}
                            noWrap={isCondensed}
                        >
                            {panelTitle()}
                        </Typography>
                    </Stack>
                    {!isCondensed && (
                        <AcxExpandableText
                            color="gray.600"
                            variant="body2"
                            mt={0.5}
                            maxTruncatedLines={2}
                        >
                            {panelDetail()}
                        </AcxExpandableText>
                    )}
                </Box>
                {showsDisabledHint && (
                    <Typography
                        id="approval-action-hint"
                        variant="caption"
                        color="gray.600"
                        textAlign="right"
                    >
                        {actionDisabledReason}
                    </Typography>
                )}
                {showsRequestApproval && (
                    <AcxButton
                        size={isCondensed ? "small" : undefined}
                        disabled={!!actionDisabledReason}
                        disabledTooltip={actionDisabledReason}
                        aria-describedby={
                            showsDisabledHint
                                ? "approval-action-hint"
                                : undefined
                        }
                        onClick={props.onRequestApproval}
                    >
                        Request approval
                    </AcxButton>
                )}
                {status === ApprovalStatus.AwaitingApproval && requester && (
                    <AcxButton
                        size={isCondensed ? "small" : undefined}
                        variant="outlined"
                        loading={isSaving}
                        onClick={props.onCancelRequest}
                    >
                        Cancel request
                    </AcxButton>
                )}
                {showsReviewVersion && (
                    <AcxButton
                        size={isCondensed ? "small" : undefined}
                        disabled={!!actionDisabledReason}
                        disabledTooltip={actionDisabledReason}
                        aria-describedby={
                            showsDisabledHint
                                ? "approval-action-hint"
                                : undefined
                        }
                        onClick={props.onReviewVersion}
                    >
                        Review version
                    </AcxButton>
                )}
                {status === ApprovalStatus.Approved && (
                    <AcxButton
                        size={isCondensed ? "small" : undefined}
                        variant="outlined"
                        onClick={props.onViewChangeRecord}
                    >
                        View change record
                    </AcxButton>
                )}
            </Stack>
        </Paper>
    );
}
